import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowLeft, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WeddingDetailPage = () => {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/weddings/${slug}`);
        setWedding(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWedding();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <p className="text-[#8B7D6B]">Loading wedding story...</p>
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex flex-col items-center justify-center">
        <p className="text-[#8B7D6B] mb-4">Wedding story not found</p>
        <Link to="/wedding-stories">
          <Button className="bg-[#DE92A9] hover:bg-[#C9758F]">
            Back to Wedding Stories
          </Button>
        </Link>
      </div>
    );
  }

  // Parse the full story content into sections
  const renderStoryContent = (content) => {
    if (!content) return null;
    
    // Check if content contains HTML
    if (content.includes('<img') || content.includes('<a href')) {
      return (
        <div 
          className="wedding-story-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatWeddingContent(content)) }}
        />
      );
    }
    
    const sections = content.split('\n\n').filter(s => s.trim());
    
    return sections.map((section, index) => {
      // Check if it's a heading (all caps or starts with specific keywords)
      const isHeading = section === section.toUpperCase() && section.length < 100;
      
      if (isHeading) {
        return (
          <h3 
            key={`${index}-${section.slice(0, 24)}`}
            className="text-2xl md:text-3xl text-[#2C2C2C] mt-12 mb-6 tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {section}
          </h3>
        );
      }
      
      return (
        <p key={`${index}-${section.slice(0, 24)}`} className="text-[#6B4E37] text-lg leading-relaxed mb-6">
          {section}
        </p>
      );
    });
  };

  // Format wedding content with HTML
  const formatWeddingContent = (content) => {
    const sections = content.split('\n\n').filter(s => s.trim());
    
    return sections.map(section => {
      if (section.includes('<img')) {
        return section;
      }
      
      const isHeading = section === section.toUpperCase() && section.length < 100 && !section.includes('<');
      
      if (isHeading) {
        return `<h3 class="text-2xl md:text-3xl text-[#2C2C2C] mt-12 mb-6 tracking-tight" style="font-family: 'Playfair Display', serif">${section}</h3>`;
      }
      
      return `<p class="text-[#6B4E37] text-lg leading-relaxed mb-6">${section}</p>`;
    }).join('');
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title={`${wedding.couple} | Real Wedding at ${wedding.venue}`}
        description={wedding.description}
        image={wedding.image}
        type="article"
        keywords={`malta wedding, real wedding, ${wedding.couple}, ${wedding.venue}, maltese wedding inspiration`}
        publishedDate={wedding.created_at}
        author="I DO Editorial Team"
        breadcrumbs={[
          { name: 'Real Weddings', url: '/wedding-stories' },
          { name: wedding.couple, url: `/wedding-stories/${wedding.slug}` },
        ]}
      />
      {/* Hero Image */}
      <div className="relative h-[70vh] overflow-hidden">
        <img 
          src={wedding.image}
          alt={wedding.couple}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Back Button */}
        <Link 
          to="/wedding-stories"
          className="absolute top-6 left-6 flex items-center gap-2 text-white hover:text-[#DE92A9] transition-colors z-10"
        >
          <ArrowLeft size={20} />
          <span className="text-sm tracking-wider">Back to Stories</span>
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="text-[#DE92A9]" size={24} fill="#DE92A9" />
            </div>
            <h1 
              className="text-4xl md:text-6xl text-white mb-4 tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {wedding.couple}
            </h1>
            <div className="flex items-center justify-center gap-6 text-white/90 text-sm tracking-wider">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{wedding.date}</span>
              </div>
              <span className="text-[#DE92A9]">|</span>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <span>{wedding.venue}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Story Content */}
      <article className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Intro Quote */}
          <div className="text-center mb-12 border-y border-[#E5E0DA] py-8">
            <p 
              className="text-2xl md:text-3xl text-[#2C2C2C] italic leading-relaxed"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              "{wedding.description}"
            </p>
          </div>

          {/* Full Story */}
          <div className="prose prose-lg max-w-none">
            {renderStoryContent(wedding.full_story)}
          </div>
        </div>

        {/* Photo Gallery */}
        {wedding.gallery && wedding.gallery.length > 0 && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <h3 
              className="text-3xl text-[#2C2C2C] text-center mb-8"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Wedding Gallery
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {wedding.gallery.map((image, index) => (
                <div 
                  key={image}
                  className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  <img 
                    src={image}
                    alt={`${wedding.couple} wedding photo ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Advertising Banner */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="bg-gradient-to-r from-[#DE92A9] to-[#E8AABC] rounded-lg p-8 md:p-12 text-center">
            <p className="text-white/80 text-xs tracking-[0.3em] uppercase mb-3">ADVERTISEMENT</p>
            <h3 
              className="text-2xl md:text-3xl text-white mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Feature Your Wedding Venue
            </h3>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Showcase your venue to thousands of engaged couples planning their dream wedding in Malta.
            </p>
            <Link to="/advertise">
              <Button className="bg-white text-[#6B4E37] hover:bg-[#FAF9F7] px-8 py-3">
                VIEW ADVERTISING OPTIONS
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#2C2C2C] mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Share Your Love Story
          </h2>
          <p className="text-[#6B4E37] mb-8">
            Recently tied the knot? We'd love to feature your beautiful wedding day in I DO Magazine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#DE92A9] hover:bg-[#C9758F] text-white px-8 py-4 text-sm tracking-wider">
                SUBMIT YOUR WEDDING
              </Button>
            </Link>
            <Link to="/wedding-stories">
              <Button variant="outline" className="border-2 border-[#DE92A9] text-[#6B4E37] hover:bg-[#DE92A9] hover:text-white px-8 py-4 text-sm tracking-wider">
                VIEW MORE STORIES
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-[#DE92A9] text-3xl"
            onClick={() => setSelectedImage(null)}
          >
            ×
          </button>
          <img 
            src={selectedImage}
            alt="Wedding photo"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default WeddingDetailPage;
